import React from 'react';
import { cn } from '../../lib/cn';
import type { DashTiles, Period } from '../../dashboard/types';
import { TRIAGE_INBOX, conversationsLink } from '../../dashboard/links';
import { DashFooterStrip, DashLink, Metric, count } from './primitives';

export interface DashHeaderProps {
  period: Period;
  asOf: string;
  windowLabel: string;
  onPeriodChange: (next: Period) => void;
}

/**
 * The page header and the period picker (spec 17 §3).
 *
 * **Two options only.** No custom ranges, no month picker, no comparison to
 * previous in V1 — and no default that varies by role. One picker, two options,
 * the same for everybody.
 */
export function DashHeader({ period, asOf, windowLabel, onPeriodChange }: DashHeaderProps) {
  return (
    <header className="mb-[22px] flex flex-wrap items-start justify-between gap-6">
      <div className="min-w-0">
        <h1 className="m-0 text-[30px] font-bold leading-[1.1] tracking-[-0.03em] text-[var(--text-primary)]">
          Dashboard
        </h1>
        <p className="m-0 mt-2 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
          What ELVA did with the practice&rsquo;s front desk{' '}
          <b className="font-semibold text-[var(--text-secondary)]">
            {period === '24h' ? 'in the last 24 hours' : 'over the last 30 days'}
          </b>
          . <b className="font-semibold text-[var(--text-secondary)]">Every number here opens the rows behind it</b> —
          nothing on this page is a figure you cannot go and read.
        </p>
      </div>

      <div className="flex shrink-0 items-start gap-4">
        <div className="text-right">
          <div className="text-[12px] font-semibold text-[var(--text-primary)]">Up to date as of {asOf}</div>
          {/* The window, in mono, so the two periods are unmistakable. */}
          <div className="mt-0.5 font-[var(--font-mono)] text-[11.5px] text-[var(--text-muted)]">{windowLabel}</div>
        </div>
        <div
          role="radiogroup"
          aria-label="Period"
          className="inline-flex shrink-0 items-center gap-1 rounded-[11px] bg-[var(--surface-sunken)] p-1"
        >
          {(['24h', '30d'] as const).map((p) => {
            const active = p === period;
            return (
              <label
                key={p}
                className={cn(
                  'cursor-pointer rounded-[8px] px-3.5 py-2 text-[13px]',
                  active
                    ? 'bg-white font-bold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]'
                    : 'font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                )}
              >
                <input
                  type="radio"
                  name="dash-period"
                  checked={active}
                  onChange={() => { onPeriodChange(p); }}
                  className="sr-only"
                />
                {p === '24h' ? 'Past 24 hours' : 'Past 30 days'}
              </label>
            );
          })}
        </div>
      </div>
    </header>
  );
}

/**
 * The four tiles (§5.1).
 *
 * **The footers are not decoration.** Each one closes an ambiguity somebody would
 * otherwise raise in a meeting. Ship the words.
 */
export function DashTilesRow({ tiles, period }: { tiles: DashTiles; period: Period }) {
  const conv = tiles.conversations;
  const afterHoursPct =
    conv.state === 'ok' && conv.value > 0 ? Math.round((tiles.afterHours / conv.value) * 100) : undefined;

  return (
    <div className="grid grid-cols-4 gap-5 max-[1100px]:grid-cols-2">
      <Tile
        label="Conversations handled"
        href={conversationsLink(period)}
        value={<Metric value={conv} zeroNote="Nothing came in during this period" />}
        sub={
          conv.state === 'ok' ? (
            <>
              <b className="font-semibold text-[var(--text-primary)]">{count(tiles.afterHours)}</b> of them outside
              opening hours{afterHoursPct !== undefined ? ` — ${String(afterHoursPct)}%` : ''}
            </>
          ) : null
        }
        footer={
          <>
            Calls, texts and email that came <i>in</i>. Outbound campaigns are counted on their own agent rows.
          </>
        }
      />

      {/* Not clickable, deliberately (§4.3). The Communication log is a record of
          CONTACTS, not of appointments, so there is no honest destination — and
          a link that half-answers is worse than no link. The sub-line and footer
          stay exactly as they are; the breakdown is still readable. */}
      <Tile
        label="Appointments created"
        value={<Metric value={tiles.appointments} zeroNote="Nothing was booked" />}
        sub={
          <>
            {count(tiles.appointmentsBy.receptionist)} AI Receptionist · {count(tiles.appointmentsBy.sms)} AI SMS ·{' '}
            {count(tiles.appointmentsBy.email)} AI Email · {count(tiles.appointmentsBy.recall)} recall ·{' '}
            {count(tiles.appointmentsBy.gapFiller)} gap filler
          </>
        }
        footer={<>Doesn’t include the {count(tiles.reschedules)} reschedules.</>}
      />

      {/* Production counts only the three agents that put a visit on the
          calendar (P333) — the TC's accepted treatment and A/R's collected money
          sit on their own agent rows. */}
      {/* Not clickable either, and for a second reason: money does not belong in
          the Communication log (spec 18 §10 — no money column). */}
      <Tile
        label="Production booked"
        value={<Metric value={tiles.productionCents} format="money" tone="var(--money-green)" />}
        sub={
          tiles.appointments.state === 'ok' ? (
            <>The scheduled value of those {count(tiles.appointments.value)} appointments</>
          ) : (
            <>The scheduled value of the appointments ELVA created</>
          )
        }
      />

      {/* A zero here is the product working, so it is green (§10.2). */}
      <Tile
        label="Needed a human"
        href={TRIAGE_INBOX}
        value={
          <Metric
            value={tiles.neededAHuman}
            tone="var(--alert-red)"
            goodZero
            zeroNote="Nobody had to step in"
          />
        }
        sub={
          tiles.neededAHuman.state === 'zero' ? null : (
            <>
              {count(tiles.warmTransfers)} warm transfers · {count(tiles.urgentTriage)} urgent triage
            </>
          )
        }
        footerTone={tiles.stillWaitingNow > 0 ? 'red' : 'plain'}
        footer={
          tiles.stillWaitingNow > 0 ? (
            <span className="flex flex-wrap items-center justify-between gap-2">
              <b className="font-semibold">
                {count(tiles.stillWaitingNow)} of them are still waiting right now.
              </b>
              <a href={TRIAGE_INBOX} className="font-semibold underline">
                Triage Inbox →
              </a>
            </span>
          ) : (
            <>Nobody is waiting for a person at this moment.</>
          )
        }
      />
    </div>
  );
}

/**
 * A tile links only where there is something honest to link to. **No `href` means
 * no hover state and no cursor change** — a dead-looking link is a worse lie than
 * a plain figure.
 */
function Tile({
  label,
  value,
  sub,
  footer,
  footerTone = 'plain',
  href,
}: {
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  footer?: React.ReactNode;
  footerTone?: 'plain' | 'red';
  href?: string;
}) {
  const body = (
    <>
      <div className="flex-1 px-5 pb-4 pt-[17px]">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
        <div className="mt-1.5">{value}</div>
        {sub ? <div className="mt-1 text-[12.5px] leading-[1.5] text-[var(--text-secondary)]">{sub}</div> : null}
      </div>
      {footer ? <DashFooterStrip tone={footerTone}>{footer}</DashFooterStrip> : null}
    </>
  );
  const shell = 'flex flex-col overflow-hidden rounded-[18px] border border-[#E7EAF2] bg-[var(--surface-card)]';
  const shadow = { boxShadow: '0 1px 3px rgba(36,41,54,0.05)' };

  if (href === undefined) {
    return (
      <div className={shell} style={shadow}>
        {body}
      </div>
    );
  }
  return (
    <a href={href} className={`${shell} hover:border-[#D6DBEA]`} style={shadow}>
      {body}
    </a>
  );
}

/** Re-exported so the page can link out without importing primitives itself. */
export { DashLink };
